// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — keeps the dataset's date (`#time-label`) at the bottom centre
 * of the globe, clear of whatever else is down there.
 *
 * The label used to sit in the top inline-end corner, where the help
 * button, the account chip and the Enter AR button also live, and at
 * every width some pair of them overlapped. The corner now holds the
 * account chip with Help under it, and the date has the one place on the
 * screen that is the same on every device: the middle of the bottom edge.
 *
 * "Bottom centre" is a column, not a fixed spot. On a desktop the middle
 * of the bottom edge is free; on a phone the playback bar, the Tools
 * buttons, the info panel's header and the legend are stacked there, and
 * they grow and shrink (an expanded info panel, a date track, a geo-media
 * panel).
 * So the label is placed by measurement: as low as it can go without
 * touching any of them. `clearBottomOffset` is that rule, pure;
 * `initTimeLabelPosition` re-runs it whenever one of them changes size,
 * is shown or hidden, or moves.
 */
import { clearPanelLegends } from './legendClearance'

/** A box in the coordinates of the label's containing block. */
export interface Box {
  left: number
  right: number
  top: number
  bottom: number
}

/**
 * How far above the container's bottom edge the label's bottom should
 * sit: `margin` when nothing is in the way, else just above the highest
 * thing it would otherwise touch. `label` gives the label's horizontal
 * extent and height; `gap` is the clear space kept around it.
 */
export function clearBottomOffset(
  containerHeight: number,
  label: { left: number; right: number; height: number },
  obstacles: readonly Box[],
  margin: number,
  gap: number,
): number {
  const inColumn = obstacles.filter(
    (box) => box.left < label.right + gap && box.right > label.left - gap,
  )
  let bottomEdge = containerHeight - margin
  // Each pass lifts the label over everything it currently touches;
  // what it lands on next is found by the following pass.
  for (let pass = 0; pass <= inColumn.length; pass++) {
    let lifted = bottomEdge
    for (const box of inColumn) {
      const touches = box.top < bottomEdge + gap && box.bottom > bottomEdge - label.height - gap
      if (touches) lifted = Math.min(lifted, box.top - gap)
    }
    if (lifted === bottomEdge) break
    bottomEdge = lifted
  }
  return containerHeight - bottomEdge
}

/** What can occupy the bottom of the screen. */
const OBSTACLE_IDS = [
  'playback-controls',
  'geo-media-panel',
  'map-controls',
  'info-panel',
  'ayni-chat-trigger',
  'ayni-chat-panel',
]

/**
 * The floating legends, one per globe. They are remade, not resized, so
 * they are not watched: main.ts calls `refreshTimeLabelPosition` after
 * drawing them.
 */
const LEGEND_SELECTOR = '.map-viewport .panel-colorbar, .map-viewport .panel-legend:not(.hidden)'

let started = false
/** Place the label again (set once started). */
let reschedule: () => void = () => {}

/** Place the label again: a legend in its corner was drawn or removed. */
export function refreshTimeLabelPosition(): void {
  reschedule()
}

/**
 * Start keeping `#time-label` clear. Idempotent; a no-op where the label
 * does not exist (tests).
 */
export function initTimeLabelPosition(): void {
  const label = document.getElementById('time-label')
  if (!label || started) return
  started = true

  let scheduled = false
  const place = (): void => {
    scheduled = false
    // AYNI: the legends first (with two or four globes they may be lifted off these controls), then the label clear of them.
    clearPanelLegends()
    if (label.classList.contains('hidden')) return
    const parent = (label.offsetParent ?? document.body).getBoundingClientRect()
    const own = label.getBoundingClientRect()
    if (own.width === 0) return
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
    const obstacles: Box[] = []
    const legends = [...document.querySelectorAll<HTMLElement>(LEGEND_SELECTOR)]
    for (const el of [...OBSTACLE_IDS.map((id) => document.getElementById(id)), ...legends]) {
      if (!el || el.classList.contains('hidden')) continue
      const rect = el.getBoundingClientRect()
      if (rect.width === 0 || rect.height === 0) continue
      obstacles.push({
        left: rect.left - parent.left,
        right: rect.right - parent.left,
        top: rect.top - parent.top,
        bottom: rect.bottom - parent.top,
      })
    }
    const offset = clearBottomOffset(
      parent.height,
      { left: own.left - parent.left, right: own.right - parent.left, height: own.height },
      obstacles,
      0.75 * rem,
      0.4 * rem,
    )
    const next = `${Math.round(offset)}px`
    if (label.style.bottom !== next) label.style.bottom = next
  }
  const schedule = (): void => {
    if (scheduled) return
    scheduled = true
    requestAnimationFrame(place)
  }
  reschedule = schedule

  // Sizes: the panels grow and shrink. Attributes: they are shown and
  // hidden by class, and moved by inline style (the Tools bar's bottom).
  const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule)
  const attributes = new MutationObserver(schedule)
  const watched = new WeakSet<Element>()
  const watch = (el: Element | null): void => {
    if (!el || watched.has(el)) return
    watched.add(el)
    resize?.observe(el)
    attributes.observe(el, { attributes: true, attributeFilter: ['class', 'style'] })
  }
  const watchAll = (): void => {
    watch(label)
    for (const id of OBSTACLE_IDS) watch(document.getElementById(id))
  }
  watchAll()
  // Some panels are created later (the geo-media panel).
  if (label.parentElement) {
    new MutationObserver(() => {
      watchAll()
      schedule()
    }).observe(label.parentElement, { childList: true })
  }
  window.addEventListener('resize', schedule)
  // The label and what is around it also slide aside for the side
  // panels (globePanelOffset.ts), by different amounts and with no
  // change of size or class to observe: measure again where they land.
  document.addEventListener('transitionend', (event) => {
    if (event.propertyName === 'translate') schedule()
  })
  schedule()
}
